/**
 * Overdue management — days from due date to payment date (or books-as-of for unpaid).
 * Penalty formula: (Overdue Days × EMI Amount) / 365
 */
import { readFinanceDefaults } from './financeCatalogs';
import {
  buildFacilityInstallmentRows,
  emiCashAppliedFromPayment,
  replayFlexibleEmiState,
  type AdminEmiPayment,
  type UnifiedCustomer,
  type UnifiedLoanFacility,
} from './workspaceStore';
import { resolveWorkspaceAsOfDate, resolveWorkspaceAsOfMs } from './workspaceAsOf';
import { parseDateOnly as parseDateOnlyYmd } from './loanScheduleDates';

export type OverduePenaltyConfig = {
  /** @deprecated Legacy flat ₹/day add-on (optional). */
  perDayRs: number;
  /** @deprecated Legacy rate — canonical formula is (days × EMI) / 365. */
  rupeesPerHundredPerMonth: number;
};

export type OverdueMissRow = {
  id: string;
  facility: string;
  recordId: string;
  kind: 'finance' | 'handloan';
  installmentNo: number;
  dueDate: string;
  paidDate: string | null;
  overdueDays: number;
  emiAmount: number;
  penaltyAmount: number;
  status: 'paid_late' | 'unpaid_overdue';
};

/** Per-installment ledger with settlement allocation (FIFO by due date). */
export type OverdueInstallmentRow = OverdueMissRow & {
  originalAmount: number;
  waivedAmount: number;
  collectedAmount: number;
  remainingBalance: number;
};

export type CustomerOverdueAnalysis = {
  rows: OverdueMissRow[];
  ledgerRows: OverdueInstallmentRow[];
  totalOverdueDays: number;
  totalPenaltyAmount: number;
  unpaidOverdueDays: number;
  unpaidPenaltyAmount: number;
  totalWaived: number;
  totalCollected: number;
  totalRemaining: number;
  paidLateCount: number;
  unpaidCount: number;
};

export function readOverduePenaltyConfig(): OverduePenaltyConfig {
  const d = readFinanceDefaults();
  return {
    perDayRs: Math.max(0, parseFloat(d.overduePenaltyPerDay) || 0),
    rupeesPerHundredPerMonth: Math.max(0, parseFloat(d.overduePenaltyPercent) || 0),
  };
}

function parseDateOnly(iso: string): string {
  return parseDateOnlyYmd(iso);
}

/** Calendar days from due date (inclusive) to compare date. */
export function daysBetweenDueAndPaid(dueDate: string, compareDate: string): number {
  const due = new Date(`${dueDate}T12:00:00`).getTime();
  const cmp = new Date(`${compareDate}T12:00:00`).getTime();
  if (!Number.isFinite(due) || !Number.isFinite(cmp)) return 0;
  return Math.max(0, Math.ceil((cmp - due) / 86400000));
}

/**
 * Canonical overdue charge: (Overdue Days × EMI Amount) / 365, rounded to paise.
 * Must match backend overdueService.calcOverduePenalty exactly — the server validates
 * waivers and settlements against it. The legacy per-browser `config` add-ons are
 * intentionally ignored so screens never show a charge the server would not accept.
 */
export function calcOverduePenalty(emiAmount: number, overdueDays: number, config?: OverduePenaltyConfig): number {
  if (overdueDays <= 0 || emiAmount <= 0) return 0;
  return Math.round(((overdueDays * emiAmount) / 365) * 100) / 100;
}

function paidCyclesForFacility(facility: UnifiedLoanFacility, payments: AdminEmiPayment[]): number {
  return replayFlexibleEmiState(facility, payments).paidCycles;
}

/**
 * Map installment index → date it was paid in full. EMI cash is applied FIFO and
 * accumulates across receipts, so partial payments and multi-EMI remainders carry
 * forward instead of being dropped (same rule as the backend overdueService).
 */
export function buildSlotPaymentMap(facility: UnifiedLoanFacility, payments: AdminEmiPayment[]): Map<number, string> {
  const periods = Math.max(1, Math.floor(Number(facility.periods) || 1));
  const emi = Math.max(0, Math.round(Number(facility.emi) || 0));
  const facilityPayments = payments
    .filter((p) => p.recordId === facility.recordId && p.entryType !== 'overdue_waive')
    .sort((a, b) => paymentTime(a.paidAt) - paymentTime(b.paidAt));

  const slotPaidAt = new Map<number, string>();
  let cumulative = 0;
  let covered = 0;

  for (const p of facilityPayments) {
    const paidDate = parseDateOnly(p.paidAt);
    if (p.facilityPayoff) {
      for (let i = 0; i < periods; i++) {
        if (!slotPaidAt.has(i) && paidDate) slotPaidAt.set(i, paidDate);
      }
      covered = periods;
      continue;
    }
    if (emi <= 0) continue;
    cumulative += emiCashAppliedFromPayment(p, emi);
    const nowCovered = Math.max(0, Math.min(periods, Math.floor((cumulative + 0.005) / emi)));
    for (let i = covered; i < nowCovered; i++) {
      if (paidDate) slotPaidAt.set(i, paidDate);
    }
    for (let i = nowCovered; i < covered; i++) slotPaidAt.delete(i);
    covered = nowCovered;
  }

  return slotPaidAt;
}

function paymentTime(paidAt: string): number {
  const t = new Date(paidAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

function booksAsOfYmd(): string {
  return (
    resolveWorkspaceAsOfDate() ||
    (() => {
      const ms = resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
      if (!ms) return '';
      const d = new Date(ms);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })()
  );
}

function analyzeFacility(
  facility: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  config: OverduePenaltyConfig
): OverdueMissRow[] {
  // Hand loans accrue their own interest (and compound after term) — the vehicle-finance
  // EMI overdue formula does not apply to them.
  if (facility.kind === 'handloan') return [];
  const paidCycles = paidCyclesForFacility(facility, payments);
  const schedule = buildFacilityInstallmentRows(facility, paidCycles, payments);
  const slotPaidAt = buildSlotPaymentMap(facility, payments);
  const today = booksAsOfYmd();
  const rows: OverdueMissRow[] = [];

  for (let i = 0; i < schedule.length; i++) {
    const slot = schedule[i];
    const dueYmd = parseDateOnly(slot.date);
    if (!dueYmd) continue;
    const paidDate = slotPaidAt.get(i) ?? null;

    if (paidDate) {
      const overdueDays = daysBetweenDueAndPaid(dueYmd, paidDate);
      if (overdueDays > 0) {
        rows.push({
          id: `${facility.recordId}-slot-${i}`,
          facility: facility.title,
          recordId: facility.recordId,
          kind: facility.kind,
          installmentNo: i + 1,
          dueDate: dueYmd,
          paidDate,
          overdueDays,
          emiAmount: slot.amount,
          penaltyAmount: calcOverduePenalty(slot.amount, overdueDays, config),
          status: 'paid_late',
        });
      }
    } else if (today && dueYmd < today) {
      if (i < paidCycles) continue;
      const overdueDays = daysBetweenDueAndPaid(dueYmd, today);
      if (overdueDays > 0) {
        rows.push({
          id: `${facility.recordId}-slot-${i}`,
          facility: facility.title,
          recordId: facility.recordId,
          kind: facility.kind,
          installmentNo: i + 1,
          dueDate: dueYmd,
          paidDate: null,
          overdueDays,
          emiAmount: slot.amount,
          penaltyAmount: calcOverduePenalty(slot.amount, overdueDays, config),
          status: 'unpaid_overdue',
        });
      }
    }
  }

  return rows;
}

type SettlementEvent = { paidAt: string; amount: number; kind: 'collect' | 'waive' };

/** Apply collections & waivers chronologically (FIFO by installment due date). */
export function allocateOverdueLedger(
  missRows: OverdueMissRow[],
  payments: AdminEmiPayment[],
  recordId: string
): OverdueInstallmentRow[] {
  const base = [...missRows]
    .filter((r) => r.recordId === recordId)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.installmentNo - b.installmentNo)
    .map((r) => ({
      ...r,
      originalAmount: r.penaltyAmount,
      waivedAmount: 0,
      collectedAmount: 0,
      remainingBalance: r.penaltyAmount,
    }));

  const events: SettlementEvent[] = [];
  for (const p of payments.filter((x) => x.recordId === recordId)) {
    const paidAt = parseDateOnly(p.paidAt);
    if (!paidAt) continue;
    const collected = Math.max(0, Number(p.lateFeeCollected) || 0);
    if (collected > 0) events.push({ paidAt, amount: collected, kind: 'collect' });
    const waived =
      Math.max(0, Number(p.overdueWaivedAmount) || 0) ||
      (p.entryType === 'overdue_waive' ? Math.max(0, Number(p.amount) || 0) : 0);
    if (waived > 0) events.push({ paidAt, amount: waived, kind: 'waive' });
  }
  events.sort((a, b) => a.paidAt.localeCompare(b.paidAt));

  for (const ev of events) {
    let left = ev.amount;
    for (const row of base) {
      if (left <= 0) break;
      if (row.remainingBalance <= 0) continue;
      const apply = Math.min(left, row.remainingBalance);
      if (ev.kind === 'collect') row.collectedAmount += apply;
      else row.waivedAmount += apply;
      row.remainingBalance = Math.round((row.remainingBalance - apply) * 100) / 100;
      left = Math.round((left - apply) * 100) / 100;
    }
  }

  return base.map((r) => ({
    ...r,
    waivedAmount: Math.round(r.waivedAmount * 100) / 100,
    collectedAmount: Math.round(r.collectedAmount * 100) / 100,
    remainingBalance: Math.max(0, Math.round(r.remainingBalance * 100) / 100),
  }));
}

export function computeCustomerOverdueAnalysis(
  customer: UnifiedCustomer,
  payments: AdminEmiPayment[],
  config?: OverduePenaltyConfig
): CustomerOverdueAnalysis {
  const cfg = config ?? readOverduePenaltyConfig();
  const recordIds = new Set(customer.facilities.map((f) => f.recordId));
  const customerPayments = payments.filter(
    (p) => p.customerId === customer.id || recordIds.has(p.recordId)
  );
  const rows: OverdueMissRow[] = [];

  for (const f of customer.facilities) {
    rows.push(...analyzeFacility(f, customerPayments, cfg));
  }

  rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.installmentNo - b.installmentNo);

  const ledgerRows: OverdueInstallmentRow[] = [];
  for (const rid of recordIds) {
    ledgerRows.push(...allocateOverdueLedger(rows, customerPayments, rid));
  }
  ledgerRows.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.installmentNo - b.installmentNo);

  const unpaidLedger = ledgerRows.filter((r) => r.remainingBalance > 0 || r.status === 'unpaid_overdue');
  const unpaidOverdueDays = unpaidLedger.reduce((s, r) => s + (r.status === 'unpaid_overdue' ? r.overdueDays : 0), 0);
  const unpaidPenaltyAmount = ledgerRows.reduce((s, r) => s + r.remainingBalance, 0);
  const totalWaived = ledgerRows.reduce((s, r) => s + r.waivedAmount, 0);
  const totalCollected = ledgerRows.reduce((s, r) => s + r.collectedAmount, 0);
  const paidLateRows = rows.filter((r) => r.status === 'paid_late');

  return {
    rows,
    ledgerRows,
    totalOverdueDays: unpaidOverdueDays,
    totalPenaltyAmount: rows.reduce((s, r) => s + r.penaltyAmount, 0),
    unpaidOverdueDays,
    unpaidPenaltyAmount: Math.round(unpaidPenaltyAmount * 100) / 100,
    totalWaived: Math.round(totalWaived * 100) / 100,
    totalCollected: Math.round(totalCollected * 100) / 100,
    totalRemaining: Math.round(unpaidPenaltyAmount * 100) / 100,
    paidLateCount: paidLateRows.length,
    unpaidCount: unpaidLedger.filter((r) => r.remainingBalance > 0).length,
  };
}

export function computeFacilityUnpaidOverdue(
  facility: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  config?: OverduePenaltyConfig
): { overdueDays: number; penaltyAmount: number } {
  const cfg = config ?? readOverduePenaltyConfig();
  const facilityPayments = payments.filter((p) => p.recordId === facility.recordId);
  const rows = analyzeFacility(facility, facilityPayments, cfg);
  const ledger = allocateOverdueLedger(rows, facilityPayments, facility.recordId);
  const unpaid = ledger.filter((r) => r.remainingBalance > 0);
  const overdueDays = unpaid.reduce((s, r) => s + (r.status === 'unpaid_overdue' ? r.overdueDays : 0), 0);
  const penaltyAmount = unpaid.reduce((s, r) => s + r.remainingBalance, 0);
  return { overdueDays, penaltyAmount: Math.round(penaltyAmount * 100) / 100 };
}

/** Workspace-wide overdue summary for the management desk. */
export function computeWorkspaceOverdueSummary(
  customers: UnifiedCustomer[],
  payments: AdminEmiPayment[]
): {
  customersWithOverdue: number;
  totalRemaining: number;
  totalWaived: number;
  totalCollected: number;
  installmentCount: number;
} {
  let customersWithOverdue = 0;
  let totalRemaining = 0;
  let totalWaived = 0;
  let totalCollected = 0;
  let installmentCount = 0;

  for (const c of customers) {
    const a = computeCustomerOverdueAnalysis(c, payments);
    if (a.totalRemaining > 0 || a.unpaidOverdueDays > 0) customersWithOverdue += 1;
    totalRemaining += a.totalRemaining;
    totalWaived += a.totalWaived;
    totalCollected += a.totalCollected;
    installmentCount += a.ledgerRows.filter((r) => r.originalAmount > 0).length;
  }

  return {
    customersWithOverdue,
    totalRemaining: Math.round(totalRemaining * 100) / 100,
    totalWaived: Math.round(totalWaived * 100) / 100,
    totalCollected: Math.round(totalCollected * 100) / 100,
    installmentCount,
  };
}

export function formatDisplayDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
